import React, { FC, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { Divider } from 'primereact/divider';
import { useMutation } from '@tanstack/react-query';
import { Messages } from 'primereact/messages';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Button, InputNumberField, KeyValuePairs } from '@/components';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { formatDateAndTime } from '@/utils/formatDate';
import { useMessage, useToastNotification } from '@/hooks';

import styles from './ModalDetails.module.scss';
import { getDetailProforma } from '../services/getDetailProforma';
import { getDocumentType, parseProformaDetails } from './ModalDetails';
import { getAddProform } from '../services/getAddProforma';
import { getObtainProform } from '../services/getObtainProforma';
import { getProformaNumber } from '../services/getProformaNumber';

type ModalDetailsProps = {
  oidLiquidacion: number;
  isDialogShown: boolean;
  toggleDialog: (val?: boolean) => void;
  tipoLiquidacion: 'DEPO20' | 'ServicioAdicional' | 'Proforma';
};

type ProformaForm = {
  numeroProforma: number | null;
};

const ModalDetailsAddProforma: FC<ModalDetailsProps> = ({
  oidLiquidacion,
  isDialogShown,
  toggleDialog,
  tipoLiquidacion,
}) => {
  const { showToast } = useToastNotification();
  const { showMessage, elementRef, clearMessages } = useMessage();

  const { t } = useTranslation('depo20');
  const [formData, setFormData] = useState<ProformaForm>({
    numeroProforma: null,
  });
  const [lockVerified, setLockVerified] = useState<boolean>(false);

  // State to manage detail data
  const [detailData, setDetailData] = useState<string | null>(null);

  const { mutate: fetchDetailData, isPending: detailLoading } = useMutation({
    mutationKey: ['ObtenerDetalle', formData.numeroProforma],
    mutationFn: getDetailProforma,
    onSuccess: (data) => {
      if (data) {
        setDetailData(data);
      }
    },
  });

  const {
    data: detailVerifyData,
    mutate: getProformaNumberMutate,
    isPending: getProformaNumberLoading,
  } = useMutation({
    mutationKey: ['getProformaNumber', formData.numeroProforma],
    mutationFn: () => getProformaNumber({ numero: formData.numeroProforma }),
    onSuccess: (res) => {
      clearMessages();

      if (res === null) {
        showMessage({
          severity: 'error',
          detail: t('enterAnotherProforma'),
          summary: t('cannotRetrieveProforma'),
        });
      }
      if (res.oid) {
        const alternativeType =
          tipoLiquidacion === 'DEPO20'
            ? t('newAdditionalServiceType')
            : 'DEPO20';

        // Definir tipos válidos para cada liquidación
        const isValidForDepo20 =
          res.clasificacionDocumento === 'PROFORMA DEPO20';
        const isValidForServicioAdicional =
          res.clasificacionDocumento === 'PROFORMA LIBRE' ||
          res.clasificacionDocumento === 'RECEPCION VACIO IMPORT';

        // Verificar si el tipo es válido para la liquidación actual
        const isValidType =
          (tipoLiquidacion === 'DEPO20' && isValidForDepo20) ||
          ((tipoLiquidacion === 'ServicioAdicional' ||
            tipoLiquidacion === 'Proforma') &&
            isValidForServicioAdicional);

        if (isValidType) {
          fetchDetailData(res.oid);
        } else {
          showMessage({
            severity: 'error',
            detail: t('proformaTypeMismatch', {
              expected:
                tipoLiquidacion === 'DEPO20'
                  ? 'DEPO20'
                  : t('additionalServiceType'),
              alternative: alternativeType,
            }),
            summary: t('invalidProformaType'),
          });
        }
      }
    },
  });

  const resetAll = () => {
    clearMessages();
    setFormData({
      numeroProforma: null,
    });
    setDetailData(null);
    setLockVerified(false);
  };

  const { mutate: fetchAddProformData, isPending: AddProformLoading } =
    useMutation({
      mutationKey: ['AddProforma', formData.numeroProforma],
      mutationFn: () =>
        getAddProform({
          oidProForma: detailVerifyData?.oid,
          oidLiquidacion,
        }),
      onSuccess: (response) => {
        resetAll();
        if (!response) {
          toggleDialog(true);
          showToast({
            severity: 'success',
            detail: 'Proforma agregada correctamente',
          });
        }
        if (response) {
          showMessage({
            severity: 'error',
            detail: t('enterAnotherProforma'),
            summary: response,
          });
        }
      },
    });

  const { mutate: fetchObtenerProformData, isPending: ObtenerProformLoading } =
    useMutation({
      mutationKey: ['ObtenerProforma', formData.numeroProforma],
      mutationFn: () =>
        getObtainProform({
          numero: detailVerifyData?.oid,
          fuente: `${oidLiquidacion}`,
        }),
      onSuccess: () => {
        fetchAddProformData();
      },
    });

  const dialogHeader = (
    <>
      <div className={`${styles.header} text-blue-800`}>
        <FontAwesomeIcon
          icon={faPlus}
          style={{ marginRight: '0.5rem' }}
          className="mr-3"
        />
        {t('addProformaButton')}
      </div>
      <Messages ref={elementRef} />
    </>
  );

  const parseSerie = detailVerifyData?.serie
    ? ` ${detailVerifyData.serie} -`
    : '';

  const keyValuePairs = [
    {
      key: 'Fecha de proforma',
      value: formatDateAndTime(detailVerifyData?.fecha ?? '-'),
    },
    {
      key: 'Documento',
      value: `${getDocumentType(detailVerifyData?.tipo ?? '')} ${parseSerie} ${detailVerifyData?.numero}`,
    },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeEvent,
    name: string
  ) => {
    const { value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleVerify = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (lockVerified) {
      resetAll();
    }
    setLockVerified(!lockVerified);
    getProformaNumberMutate();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    fetchObtenerProformData();
  };

  const { details, total } = parseProformaDetails(detailData ?? '');

  return (
    <Dialog
      header={dialogHeader}
      visible={isDialogShown}
      style={{
        maxWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
        minWidth: window.innerWidth <= 1200 ? '95vw' : '1200px',
      }}
      onHide={() => {
        toggleDialog();
        resetAll();
      }}
      modal
      breakpoints={{ '1200px': '95vw' }}
    >
      <div className="p-4">
        <form onSubmit={(e) => handleSubmit(e)}>
          <div className="flex flex-column md:flex-row align-items-end mt-0 mb-0">
            <InputNumberField
              id="numeroProforma"
              name="numeroProforma"
              value={formData.numeroProforma}
              onChange={(e) => handleInputChange(e, 'numeroProforma')}
              placeholder={t('proformaNumberExample')}
              label={t('proformaNumberHeader')}
              classnames="col-12 md:col-3 pl-0  mb-0"
              disabled={lockVerified}
            />
            <Button
              className={`mt-4 sm:mt-0 ml-3 col-12 md:col-3 ${styles.verifyProforma} mb-0`}
              onClick={(e) => handleVerify(e)}
              loading={detailLoading || getProformaNumberLoading}
            >
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              {lockVerified
                ? t('verifyAnotherProformaButton')
                : t('verifyProformaButton')}
            </Button>
          </div>
          {detailData && (
            <>
              <Divider className={styles.paymentDivider} />

              <div
                className={`${styles.modalTitle} my-3 font-bold text-blue-800`}
              >
                {t('proformDetail')}
              </div>
              <KeyValuePairs pairs={keyValuePairs} />

              <ul className={`${styles.list} ${styles.listAddProforma}`}>
                {details?.map(
                  (result) =>
                    result && (
                      <React.Fragment key={`${result?.key}`}>
                        <li>
                          <span
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{ __html: result?.key }}
                          />
                          <div className={styles.amount}>
                            {result?.amount?.toFixed(2)}
                          </div>
                        </li>
                        <hr />
                      </React.Fragment>
                    )
                )}
              </ul>
              {total && (
                <div className={styles.divTotalContainer}>
                  <strong>{t('totalLabel')}</strong>
                  <div className={styles.totalAmount}>
                    <h6>U$S</h6>
                    <strong>{total}</strong>
                  </div>
                </div>
              )}

              <Divider className={styles.paymentDivider} />
              <div className="flex justify-content-end mt-4">
                <button
                  type="button"
                  onClick={() => {
                    toggleDialog();
                    resetAll();
                  }}
                  className={`${styles.cancelButton} mr-4`}
                >
                  {t('cancelButton')}
                </button>
                <Button
                  className={`${styles.selectButton}`}
                  type="submit"
                  loading={ObtenerProformLoading || AddProformLoading}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    style={{ marginRight: '0.5rem' }}
                    className="mr-3"
                  />
                  {t('addButton')}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </Dialog>
  );
};

export default ModalDetailsAddProforma;
